// ============ Human Design — the structural reading ============
//
// The chart already said WHAT is switched on. These panels say what that
// wiring means: which circuits the reader is actually plugged into, what
// "Split Definition" looks like in their own chart and which single gate would
// close it, every centre with the gates it holds, and the Incarnation Cross as
// four named gates instead of one opaque string.
//
// Same rule as the BaZi and I Ching tabs: the numbers are computed (here in
// `src/lib/hdStructure.ts`, all graph work over the 36 channels) and only the
// short "what it means" lines are reading, which is why they are labelled.

import React, { useMemo, useState } from 'react';
import { useT, useLang } from '../lib/i18n';
import { GATE_TH, GATE_EN } from '../lib/humandesign';
import {
  CENTRES, circuitTally, readDefinition, centreGates, crossGates, lineSpread,
  GATE_CIRCUIT, CIRCUIT_BY_ID,
} from '../lib/hdStructure';
import { CHANNEL_READ, channelKey } from '../lib/destinyRead';
import { quarterOfGate, channelConsciousness } from '../lib/hdDetail';
import { SecHead } from './core';

const CENTRE_TH: Record<string, string> = Object.fromEntries(CENTRES.map(c => [c.id, c.th]));
const CENTRE_EN: Record<string, string> = Object.fromEntries(CENTRES.map(c => [c.id, c.en]));

export function HdStructure({ hd, definedCenters }: { hd: any; definedCenters: string[] }) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [openCircuit, setOpenCircuit] = useState<string | null>(null);
  const [openCentre, setOpenCentre] = useState<string | null>(null);

  const gateName = (g: number) => (en ? GATE_EN[g] : GATE_TH[g]) || `Gate ${g}`;
  const centreName = (c: string) => (en ? CENTRE_EN[c] : CENTRE_TH[c]) || c;

  const tally = useMemo(() => circuitTally(hd.definedChannels), [hd.definedChannels]);
  const def = useMemo(() => readDefinition(hd.definedChannels, hd.activeGates), [hd.definedChannels, hd.activeGates]);
  const cross = useMemo(() => crossGates(hd.personality, hd.design), [hd.personality, hd.design]);
  const persSun = hd.personality.find((a: any) => a.k === 'sun');
  const desSun = hd.design.find((a: any) => a.k === 'sun');
  const qPers = persSun ? quarterOfGate(persSun.gate) : null;
  const qDes = desSun ? quarterOfGate(desSun.gate) : null;
  const persGates = useMemo(() => hd.personality.map((a: any) => a.gate), [hd.personality]);
  const desGates = useMemo(() => hd.design.map((a: any) => a.gate), [hd.design]);
  const spread = useMemo(() => lineSpread([...hd.personality, ...hd.design]), [hd.personality, hd.design]);
  const defined = useMemo(() => new Set(definedCenters), [definedCenters]);
  const maxCh = Math.max(1, ...tally.map(x => x.channels.length));
  const maxLine = Math.max(1, ...spread);

  const DEF_LABEL: Record<string, [string, string]> = {
    none: ['ไม่มีช่องที่สมบูรณ์ — คุณสะท้อนสิ่งแวดล้อมทั้งหมด', 'No complete channel — you reflect the environment entirely'],
    single: ['ก้อนเดียว — พลังงานไหลถึงกันหมด ตัดสินใจได้ด้วยตัวเองโดยไม่ต้องรออะไรมาต่อ', 'One island — energy reaches all of it; you can decide without waiting to be completed'],
    split: ['สองก้อนแยกกัน — มีช่องว่างที่คุณจะรู้สึกว่า "ยังไม่ครบ" จนกว่าจะเจอคนหรือช่วงเวลาที่มาต่อให้', 'Two islands — a gap you feel as incompleteness until a person or a transit bridges it'],
    triple: ['สามก้อน — ต้องใช้เวลาและคนหลายคนกว่าจะรู้สึกเชื่อมกัน อย่าเร่งตัวเอง', 'Three islands — it takes time and more than one person to feel joined; do not rush yourself'],
    quadruple: ['สี่ก้อน — หายากมาก ยิ่งต้องให้เวลาตัวเองในการตัดสินใจ', 'Four islands — rare; give decisions even more time'],
  };

  return (
    <React.Fragment>
      {/* ---------------------------------------------------- circuitry ---- */}
      <div className="mm-card glass">
        <SecHead th="วงจรพลังงาน · คุณถูกต่อเข้ากับอะไร" en="CIRCUITRY · what you are wired into"
          right={<span className="mono dim">{hd.definedChannels.length} {t('ช่อง', 'channels')}</span>}></SecHead>
        {tally.length === 0 ? (
          <p className="dim">{t('ไม่มีช่องที่สมบูรณ์ — ไม่มีวงจรใดคงที่ในตัวคุณ (Reflector)', 'No complete channel — no circuit is fixed in you (Reflector).')}</p>
        ) : (
          <React.Fragment>
            <div className="mm-hds-bars">
              {tally.map(({ meta, channels }) => (
                <button key={meta.id} className={`mm-hds-bar ${openCircuit === meta.id ? 'on' : ''}`}
                  onClick={() => setOpenCircuit(openCircuit === meta.id ? null : meta.id)}>
                  <span className="mm-hds-bname" style={{ color: meta.colour }}>{en ? meta.en : meta.th}</span>
                  <i style={{ width: `${(channels.length / maxCh) * 100}%`, background: meta.colour }}></i>
                  <em className="mono">{channels.length}</em>
                </button>
              ))}
            </div>
            {openCircuit && (() => {
              const meta = CIRCUIT_BY_ID.get(openCircuit as any)!;
              const chans = tally.find(x => x.id === openCircuit)!.channels;
              return (
                <div className="mm-hds-open" style={{ borderColor: meta.colour + '55' }}>
                  <p className="mm-hds-about"><b style={{ color: meta.colour }}>{en ? meta.en : meta.th}</b> — {en ? meta.aboutEn : meta.aboutTh}</p>
                  <p className="mm-hds-market">{en ? meta.marketEn : meta.marketTh}</p>
                  <div className="mm-chan-list">
                    {chans.map(([a, b]) => {
                      const k = channelKey(a, b);
                      const r = CHANNEL_READ[k];
                      // Both ends conscious, both unconscious, or one of each —
                      // the same channel is a different experience each way.
                      const c = channelConsciousness(a, b, persGates, desGates);
                      const CONS: Record<string, [string, string, string]> = {
                        conscious: ['รู้ตัว', 'conscious', 'good'],
                        unconscious: ['ร่างกายทำเอง', 'unconscious', 'warn'],
                        mixed: ['ครึ่งรู้ตัว', 'half-conscious', 'gold'],
                      };
                      return (
                        <div className="mm-chan-row" key={k}>
                          <b className="mono">{k}</b>
                          <span>
                            {r ? (en ? r.en : r.th) : `${gateName(a)} ↔ ${gateName(b)}`}
                            <i className={`mm-ich-chip ${CONS[c][2]}`} style={{ marginLeft: 6 }}>{en ? CONS[c][1] : CONS[c][0]}</i>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </React.Fragment>
        )}
        <p className="mm-footnote dim">{t(
          'วงจรคือ "ทำไม" ของช่องที่คุณมี — ปัจเจกคือกลายพันธุ์ ส่วนรวมคือแบ่งปันรูปแบบ เผ่าคือดูแลกันเอง · กดที่แถบเพื่อดูช่องในวงจรนั้น',
          'A circuit is the WHY behind your channels — individual mutates, collective shares patterns, tribal looks after its own. Tap a bar for the channels in it.')}</p>
      </div>

      {/* -------------------------------------------------- definition ---- */}
      <div className="mm-grid-2">
        <div className="mm-card glass">
          <SecHead th="Definition · พลังงานของคุณต่อกันเป็นกี่ก้อน" en="DEFINITION · how many islands your energy forms"
            right={<span className="mono dim">{def.groups.length} {def.groups.length === 1 ? t('ก้อน', 'island') : t('ก้อน', 'islands')}</span>}></SecHead>
          <div className="mm-hds-groups">
            {def.groups.map((g, i) => (
              <div className="mm-hds-group" key={i}>
                <b className="mono">{t('ก้อนที่', 'Island')} {i + 1}</b>
                <div>{g.map(c => <span className="mm-hds-cc" key={c}>{centreName(c)}</span>)}</div>
              </div>
            ))}
            {def.groups.length === 0 && <p className="dim">{DEF_LABEL.none[en ? 1 : 0]}</p>}
          </div>
          {def.groups.length > 0 && <p className="mm-hds-about">{DEF_LABEL[def.label]?.[en ? 1 : 0]}</p>}
          {def.bridges.length > 0 && (
            <React.Fragment>
              <b className="mono dim" style={{ display: 'block', margin: '10px 0 6px', fontSize: '0.56rem', letterSpacing: '0.1em' }}>
                {t('ประตูที่จะเชื่อมก้อนเข้าด้วยกัน', 'Gates that would join the islands')}
              </b>
              <div className="mm-hds-bridges">
                {def.bridges.map(b => (
                  <span className="mm-hds-bridge" key={`${b.gate}-${b.withGate}`}>
                    <b className="mono">{b.gate}</b> {gateName(b.gate)}
                    <em className="dim"> · {t('ต่อกับ', 'pairs with')} {b.withGate} ({centreName(b.joins[0])} ↔ {centreName(b.joins[1])})</em>
                  </span>
                ))}
              </div>
              <p className="mm-footnote dim">{t(
                'คุณถือปลายข้างหนึ่งไว้แล้ว — ประตูเหล่านี้คือครึ่งที่ขาด คนที่มีมันจะทำให้คุณรู้สึก "ครบ" และนั่นคือเหตุผลที่บางคนทำให้คุณกล้าตัดสินใจผิดจังหวะ',
                'You already hold one end — these are the missing halves. People who carry them make you feel complete, which is exactly why they can talk you into a badly timed decision.')}</p>
            </React.Fragment>
          )}
        </div>

        {/* -------------------------------------- incarnation cross ---- */}
        <div className="mm-card glass">
          <SecHead th="Incarnation Cross · สี่ประตูที่เป็นแกนชีวิต" en="INCARNATION CROSS · the four gates at the spine"
            right={<span className="mono dim">{hd.cross}</span>}></SecHead>
          <div className="mm-hds-cross">
            {cross.map(({ role, roleTh, a }) => (
              <div className="mm-hds-crossrow" key={role}>
                <span className="mm-hds-crole mono dim">{en ? role : roleTh}</span>
                <b className="mono">{a.gate}.{a.line}</b>
                <span>{gateName(a.gate)}</span>
                <em className="dim">{en ? CIRCUIT_BY_ID.get(GATE_CIRCUIT[a.gate])!.en : CIRCUIT_BY_ID.get(GATE_CIRCUIT[a.gate])!.th}</em>
              </div>
            ))}
          </div>
          {/* The quarter is a 90° slice of the wheel, so it follows from the
              Sun's gate — but which Sun is doctrine, not arithmetic, and the
              two readings disagree here often enough to matter. Both are shown
              rather than one picked on the reader's behalf. */}
          {qPers && (
            <div className="mm-hds-quarter" style={{ borderColor: qPers.colour + '55' }}>
              <b style={{ color: qPers.colour }}>{en ? qPers.en : qPers.th}</b>
              <span>{en ? qPers.themeEn : qPers.themeTh}</span>
              <small className="dim">
                {t('อ่านจากดวงอาทิตย์ฝั่ง Personality', 'read from the Personality Sun')}
                {qDes && qDes.id !== qPers.id
                  ? ` · ${t('บางสำนักอ่านจากฝั่ง Design ซึ่งของคุณคือ', 'some schools read the Design Sun, which for you is')} ${en ? qDes.en : qDes.th}`
                  : qDes ? ` · ${t('ฝั่ง Design ให้จตุภาคเดียวกัน', 'the Design Sun gives the same quarter')}` : ''}
              </small>
            </div>
          )}
          <p className="mm-footnote dim">{t(
            'ไม้กางเขนอ่านจากดวงอาทิตย์และโลกทั้งสองฝั่ง — สองประตูแรกคือสิ่งที่คุณรู้ตัว สองประตูหลังคือสิ่งที่ร่างกายทำอยู่แล้วโดยไม่ต้องสั่ง',
            'The cross is read from the Sun and Earth on both sides — the first two are what you are aware of, the last two are what the body already does unasked.')}</p>
        </div>
      </div>

      {/* ---------------------------------------------- nine centres ---- */}
      <div className="mm-card glass">
        <SecHead th="ศูนย์ทั้ง 9 · ประตูที่คุณถือในแต่ละศูนย์" en="ALL NINE CENTRES · the gates you hold in each"
          right={<span className="mono dim">{definedCenters.length}/9 {t('กำหนด', 'defined')}</span>}></SecHead>
        <div className="mm-hds-centres">
          {CENTRES.map(c => {
            const { gates, on } = centreGates(c.id, hd.activeGates);
            const isDef = defined.has(c.id);
            const open = openCentre === c.id;
            return (
              <div className={`mm-hds-centre ${isDef ? 'def' : ''} ${open ? 'open' : ''}`} key={c.id}
                onClick={() => setOpenCentre(open ? null : c.id)}>
                <div className="mm-hds-chead">
                  <b>{en ? c.en : c.th}</b>
                  <span className={`mm-hds-tag ${isDef ? 'on' : ''}`}>{isDef ? t('กำหนด', 'defined') : t('เปิด', 'open')}</span>
                  <em className="mono dim">{on.length}/{gates.length}</em>
                </div>
                <small className="dim">{en ? c.roleEn : c.roleTh}</small>
                <div className="mm-hds-gates">
                  {gates.map(g => (
                    <span key={g} className={`mm-hds-gate ${on.includes(g) ? 'on' : ''}`}
                      title={gateName(g)}>{g}</span>
                  ))}
                </div>
                {open && (
                  <div className="mm-hds-gnames">
                    {gates.map(g => (
                      <p key={g} className={on.includes(g) ? 'on' : ''}>
                        <b className="mono">{g}</b> {gateName(g)}
                        <em className="dim"> · {en ? CIRCUIT_BY_ID.get(GATE_CIRCUIT[g])!.en : CIRCUIT_BY_ID.get(GATE_CIRCUIT[g])!.th}</em>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="mm-footnote dim">{t(
          'ตัวเลขที่สว่างคือประตูที่ดาวของคุณเปิดไว้จริง · ศูนย์ที่ "กำหนด" ทำงานเหมือนกันทุกวัน ศูนย์ที่ "เปิด" คือที่ที่คุณรับพลังของคนอื่นเข้ามาและขยายมันโดยไม่รู้ตัว · กดเพื่อดูชื่อประตูทั้งหมด',
          'Lit numbers are gates your planets actually opened. A defined centre behaves the same every day; an open one is where you take in other people\'s energy and amplify it without noticing. Tap for the full gate names.')}</p>
      </div>

      {/* --------------------------------------------- line spread ---- */}
      <div className="mm-card glass">
        <SecHead th="เส้นทั้ง 6 · โปรไฟล์พูดแทนชาร์ตได้แค่ไหน" en="THE SIX LINES · how much of the chart your profile speaks for"
          right={<span className="mono dim">{hd.profile}</span>}></SecHead>
        <div className="mm-ich-linebars">
          {spread.map((n, i) => (
            <div className="mm-ich-lb" key={i}>
              <span className="mono">{t('เส้น', 'line')} {i + 1}</span>
              <i style={{ width: `${(n / maxLine) * 100}%` }}></i>
              <em className="mono">{n}</em>
            </div>
          ))}
        </div>
        <p className="mm-footnote dim">{t(
          'โปรไฟล์มาจากเส้นของดวงอาทิตย์สองฝั่งเท่านั้น — อีก 24 ตำแหน่งที่เหลือก็มีเส้นของมันเหมือนกัน แถบนี้คือทั้งชาร์ต และเป็นชุดตัวเลขเดียวกับที่แท็บอี้จิงใช้',
          'Your profile comes from the Sun on both sides alone — the other 24 activations have line positions too. This is the whole chart, and it is the same tally the I Ching tab draws.')}</p>
      </div>
    </React.Fragment>
  );
}
